import React, { cloneElement, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom';
import * as Dialog from '@/components/ui/modal/Dialog';
import { useTranslation } from 'react-i18next';
import { Check, ChevronRight, X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextButton } from '@/components/common/text-button';
import { SearchBtnIcon } from '@/components/agent-dashboard/assets';
import { TextInput } from '@/components/common/text-input';
import { TruncatedText } from '@/components/common/truncated-text';
import { Image } from '@/components/common/image';
import { CreateActorButton } from '../custom-digital-human';
import { AdminVideoTemplateModal } from '../agent-dashboard-company-asset/admin-video-template-modal';
import { ActorCard } from './actor-card';
import { EditVideoPlayModal } from '../modal-edit-video-play';
import { AllActorModal } from './all-actor-modal';
import { useToast } from '@/hooks/use-toast';
import { agentCompanyAssetHooks, useUserManagementHooks } from '@/manager';
import { LoadingOverlay } from '../common/loading-overlay';
import { StyleData } from '@/services/agent-v2-style-list-local';
import { AdminVideoTemplateListItemData } from '@/services/admin-video-template-list';
import { AgentUserActorData } from '@/manager/manager/agent-dashboard-user-management-manager';
import { AddTemplateActiveIcon } from './assets';

const toStyleData = (item: AdminVideoTemplateListItemData): StyleData => ({
  styleId: item.styleId,
  name: item.name,
  coverUrl: item.coverUrl,
  demoUrl: item.demoUrl || '',
  scene: item.scene,
});

interface TemplateVideoAddModalProps {
  actors: Actor[];
  voices: Audio[];
  trigger?: React.ReactElement;
  title?: string;
  open?: boolean;
  loading?: boolean;
  selectedStyle?: StyleData | null;
  selectedActor?: Actor | null;
  onSave?: (data: { data: StyleData | null, actor: Actor | null }) => Promise<void>;
  onUpdate?: (data: { data: StyleData | null, actor: Actor | null }) => Promise<void>;
  onOpenChange?: (open: boolean) => void;
  onSelectStyle?: (style: StyleData | null) => void;
}

/** 新增模板弹窗 */
export const TemplateVideoAddModal: React.FC<TemplateVideoAddModalProps> = ({
  trigger,
  actors,
  voices,
  title,
  open: externalOpen,
  onOpenChange,
  loading: externalLoading,
  selectedStyle,
  selectedActor,
  onSave,
  onUpdate,
  onSelectStyle,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { id: agentId } = useParams();

  const [internalOpen, setInternalOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null); // 用于聚焦标题的ref

  const {
    loading,
    adminVideoTemplates,
    adminVideoTemplatesSearchForm,
    changeSearchForm,
    search,
  } = agentCompanyAssetHooks.useAdminVideoTemplatesData(agentId);

  const { addActor } = useUserManagementHooks.useActorAction();
  const { regenerateActor } = useUserManagementHooks.useRegenerateActor();
  const { deleteActor } = useUserManagementHooks.useDeleteActor();

  const [initialSearch, setInitialSearch] = useState(false);

  const [selectedStyleId, setSelectedStyleId] = useState<string | null>(null);
  const [selectedActorId, setSelectedActorId] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = async (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
    setSelectedStyleId(null);
    setSelectedActorId(null);
  };

  const handleInitSearch = async () => {
    try {
      setInitialSearch(true)
      await search();
    } catch (error) {
      console.log('handleInitSearch error', error);
    } finally {
      setInitialSearch(false);
    }
  }

  useEffect(() => {
    if (currentOpen) {
      handleInitSearch();
    } else if (adminVideoTemplatesSearchForm.templateId) {
      changeSearchForm('templateId', '');
      changeSearchForm('page', 1);
      void search();
    }
  }, [currentOpen]);

  // 处理风格选择
  const handleStyleSelect = (id: string) => {
    setSelectedStyleId(prev => {
      if (prev === id) {
        return null; // 取消选择
      } else {
        return id; // 添加选择
      }
    });
  };

  const handleSearch = async () => {
    try {
      changeSearchForm('page', 1);
      await search();
    } catch (error) {
      console.warn(error);
    } finally {
      setSelectedStyleId(null);
    }
  }

  // 处理风格弹窗选择（后台视频模板）
  const handleStyleModalSelect = async (selectedList: AdminVideoTemplateListItemData[]) => {
    const data = selectedList[0];
    if (!data) return;
    setSelectedStyleId(data.styleId);
    onSelectStyle?.(toStyleData(data));
  }

  // 处理数字人弹窗选择
  const handleActorModalSelect = (actor: AgentUserActorData) => {
    // 如果选择的不在前五个，将选择的数字人数据塞到列表第一个位置
    if (!actors.slice(0, 5).some(item => item.id === actor.id)) {
      const newActor = actors.filter(item => item.id !== actor.id);
      addActor([actor, ...newActor as AgentUserActorData[]]);
      setSelectedActorId(actor.id);
      return;
    }
    setSelectedActorId(actor.id);
  }

  // 处理数字人选择
  const handleActorSelect = (actor: Actor) => {
    if (actor.generateStatus !== 2) return;
    setSelectedActorId(prev => {
      if (prev === actor.id) {
        return null; // 取消选择
      } else {
        return actor.id; // 添加选择
      }
    });
  };

  const handleSave = async () => {
    if (!selectedStyleId || !selectedActorId) {
      toast({
        description: t('agent_dashboard.user_detail.template.add_modal.save_tips'),
        variant: 'destructive',
      });
      return;
    }
    const selectedTemplate = adminVideoTemplates.find((item) => item.styleId === selectedStyleId);
    await onSave?.({
      data: selectedTemplate ? toStyleData(selectedTemplate) : null,
      actor: actors.find(actor => actor.id === selectedActorId),
    });
    handleClose();
  }

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 弹窗打开时，强制聚焦到标题
  useEffect(() => {
    if (currentOpen) {
      // 等待DOM更新后执行聚焦（确保元素已挂载）
      setTimeout(() => {
        titleRef.current?.focus();
      }, 0);
    }
  }, [currentOpen]);

  const handleVideoClick = (id: string) => {
    setSelectedVideoIndex(actors.findIndex(actor => actor.id === id));
    setModalOpen(true);
  };

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[720px] h-[727px]  flex-shrink-0",
          "z-50 overflow-hidden"
        )}>
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6  border-b border-[rgba(233,234,235,1)] pb-5">
          <Dialog.Title
            ref={titleRef}
            tabIndex={-1}
            className='focus:outline-none text-[rgba(24,29,39,1)] text-lg font-medium flex items-center'
          >
            <div className='size-12 flex justify-center items-center rounded-lg mr-4 border border-[rgba(213,215,218,1)]'>
              <AddTemplateActiveIcon className='size-6' />
            </div>
            <span>{title || t('agent_dashboard.user_detail.template.add_modal.title')}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
            </button>
          </Dialog.Close>
        </div>
        {/* 内容 */}
        <div className='flex-1 flex flex-col px-6 pt-5 select-none overflow-y-auto mb-5'>
          {/* 风格区域 */}
          <div>
            <div className='flex items-center justify-between mb-4'>
              <span className='text-[rgba(24,29,39,1)] text-base font-medium'>
                {t('agent_dashboard.user_detail.template.add_modal.choose_style')}
              </span>
              <AdminVideoTemplateModal
                showPresetTag
                agentId={agentId}
                trigger={(
                  <button className='group flex items-center justify-center gap-1 rounded-lg '>
                    <span className='text-[rgba(102,112,133,1)] text-sm font-medium group-hover:text-[rgba(24,29,39,1)]'>{t('agent_dashboard.user_detail.template.add_modal.view_all')}</span>
                    <ChevronRight className='size-5 text-[rgba(102,112,133,1)] group-hover:text-[rgba(24,29,39,1)] transition-all' />
                  </button>
                )}
                onSave={handleStyleModalSelect}
              />
            </div>
            <div className='flex items-center gap-4 mb-5'>
              <TextInput
                type="text"
                placeholder={t('agent_dashboard.user_detail.template.add_modal.search_placeholder')}
                value={adminVideoTemplatesSearchForm.templateId}
                className='h-10 !font-normal'
                onChange={(e) => changeSearchForm('templateId', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
              <TextButton
                label={t('agent_dashboard.user_detail.template.add_modal.search')}
                icon={<SearchBtnIcon className='w-5 h-5' />}
                className={cn(
                  '!w-[106px] h-9 ',
                  'text-white font-semibold text-sm',
                )}
                onClick={handleSearch}
              />
            </div>
            {initialSearch && <Skeleton />}
            {!initialSearch && <div className='grid grid-cols-6 gap-3 relative h-[172px]'>
              {adminVideoTemplates?.slice(0, 6)?.map((item) => (
                <div key={item.id} className="w-full h-full relative" onClick={() => onSelectStyle?.(toStyleData(item))}>
                  <div
                    className={cn(
                      "w-[102px] h-[136px] group relative rounded-[12px] transition-all cursor-pointer p-0.5 overflow-hidden select-none",
                      selectedStyleId === item.styleId
                        ? "outline outline-2 outline-i-normal"
                        : "outline outline-transparent"
                    )}
                    onClick={() => handleStyleSelect(item.styleId)}
                  >
                    <div className='size-full overflow-hidden flex-shrink-0'>
                      <Image src={item.coverUrl} className="size-full rounded-[12px] object-cover" />
                      {item.templateResource === 1 && <span className='absolute top-2 left-2 bg-black/40 text-[rgba(255,255,255,1)] text-xs font-medium rounded-[4px] px-1 py-0.5'>{t('agent_dashboard.user_detail.template.add_modal.preset')}</span>}
                    </div>
                    {/* 选中样式 */}
                    <div
                      className={cn(
                        "size-11 bg-black  absolute -bottom-5 -right-5 opacity-0 transition-all duration-150 rounded-tl-[18.4px] rounded-br-[10px] rounded-tr-none rounded-bl-none",
                        selectedStyleId === item.styleId && "opacity-100"
                      )}
                    >
                    </div>
                    <Check className={cn(
                      "size-4 text-white absolute bottom-0.5 right-1 font-bold opacity-0",
                      selectedStyleId === item.styleId && "opacity-100"
                    )} />
                  </div>
                  <div className="w-full text-center mt-3">
                    <div className="w-full">
                      <TruncatedText text={item.name} className='max-w-[90%] text-sm text-[#333333]' />
                    </div>
                  </div>
                </div>
              ))}
              <LoadingOverlay loading={loading} />
            </div>}
          </div>
          {/* 选择数字人区域 */}
          <div className='mt-5'>
            <div className='flex items-center justify-between mb-4'>
              <span className='text-[rgba(24,29,39,1)] text-base font-medium'>
                {t('agent_dashboard.user_detail.template.add_modal.choose_actor')}
              </span>
              <AllActorModal
                actors={actors}
                onSave={handleActorModalSelect}
                selectedId={selectedActorId}
                voices={voices}
                trigger={(
                  <button className='group flex items-center justify-center gap-1 rounded-lg '>
                    <span className='text-[rgba(102,112,133,1)] text-sm font-medium group-hover:text-[rgba(24,29,39,1)]'>{t('agent_dashboard.user_detail.template.add_modal.view_all')}</span>
                    <ChevronRight className='size-5 text-[rgba(102,112,133,1)] group-hover:text-[rgba(24,29,39,1)] transition-all' />
                  </button>
                )}
              />
            </div>
            <div className='mt-4 grid grid-cols-6 gap-3'>
              <CreateActorButton className='w-[100px] h-[136px]' textClassName='text-xs mt-1' onCreate={() => { }} />
              {actors.slice(0, 5).map((actor, index) => {
                return (
                  <div className='w-[100px] h-[136px] relative'>
                    <ActorCard
                      key={`${actor.id}_${index}`}
                      actor={actor}
                      name={actor.name}
                      url={actor.video}
                      poster={actor.image}
                      // isSelected={selectedActorId === actor.id}
                      status={actor.generateStatus}
                      createdAt={actor.createdAt}
                      onClick={() => handleActorSelect(actor)}
                      onEdit={() => actor.video && handleVideoClick(actor.id)}
                      onRegenerate={() => regenerateActor(actor)}
                      onDelete={() => deleteActor(actor.id)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {modalOpen && (
          <EditVideoPlayModal
            open={modalOpen}
            videos={actors}
            initialIndex={selectedVideoIndex}
            onOpenChange={setModalOpen}
            voices={voices}
          />
        )}
        {/* 操作按钮区域 */}
        <div className='w-full h-[93px] flex justify-end p-6 gap-2 border-t border-[rgba(233,234,235,1)]'>
          {/* <TextButton
            label={t('agent_dashboard.user_detail.template.add_modal.cancel')}
            className='w-[135px] h-11 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-semibold text-base !text-[rgba(65,70,81,1)]'
            onClick={handleClose}
          />
          <TextButton
            label={t('agent_dashboard.user_detail.template.add_modal.save')}
            className='w-[135px] h-11'
            onClick={handleSave}
            loading={externalLoading}
            disabled={externalLoading}
          /> */}
          <TextButton
            label={t('common.close')}
            className='w-[135px] h-11 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-semibold text-base !text-[rgba(65,70,81,1)]'
            onClick={handleClose}
          />
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}
const Skeleton: React.FC = () => {
  return (
    <div className="grid grid-cols-6 gap-3 relative">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="w-full h-full relative">
          <div
            className="w-[102px] h-[136px] p-0.5 relative rounded-[12px] overflow-hidden select-none animate-pulse"
          >
            {/* 卡片骨架 */}
            <div className="size-full bg-gray-200 rounded-[12px]"></div>
          </div>
          {/* 标题骨架 */}
          <div className="w-full text-center mt-3">
            <div className="w-[80%] h-6 bg-gray-200 rounded mx-auto animate-pulse"></div>
          </div>
        </div>
      ))}
    </div>
  );
};